import { formatInTimezone } from '../timezone/utils';
function toNum(v) {
    if (v === undefined || v === null)
        return NaN;
    const n = typeof v === 'string' ? parseFloat(v) : Number(v);
    return Number.isFinite(n) ? n : NaN;
}
export function formatUsdAmount(n) {
    if (!Number.isFinite(n))
        return '—';
    const abs = Math.abs(n);
    const s = abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (n > 1e-9)
        return `+$${s}`;
    if (n < -1e-9)
        return `-$${s}`;
    return `$${s}`;
}
export function formatUsdPlain(n) {
    if (!Number.isFinite(n))
        return '—';
    return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
}
export function formatUsdCurrency(n) {
    if (!Number.isFinite(n))
        return '—';
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
export function usdLedgerCreatedAt(row, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
    var _a;
    const v = (_a = row.created_at) !== null && _a !== void 0 ? _a : row.createdAt;
    return formatInTimezone(v, timezone);
}
/** Compact created display: date line + 24h time line (e.g. May 04, 2026 / 19:57). */
export function usdLedgerCreatedAtCompact(row, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
    var _a;
    const v = (_a = row.created_at) !== null && _a !== void 0 ? _a : row.createdAt;
    if (!v)
        return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime()))
        return null;
    const dateLine = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        month: 'short',
        day: '2-digit',
        year: 'numeric',
    }).format(d);
    const timeLine = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).format(d);
    return { dateLine, timeLine };
}
/** Optional source/provider label when distinct from payment method. */
export function usdLedgerSourceLabel(row) {
    var _a;
    const provider = String((_a = row.provider) !== null && _a !== void 0 ? _a : '').trim();
    const meta = row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
    const metaSource = meta.source != null ? String(meta.source).trim() : '';
    const candidate = provider || metaSource;
    if (!candidate)
        return null;
    const pm = usdLedgerPaymentMethodLabel(row);
    if (pm !== '—' && candidate.toLowerCase() === pm.toLowerCase())
        return null;
    return candidate;
}
export function isPurchaseCreditsRow(row) {
    var _a, _b, _c;
    const t = String((_a = row.type) !== null && _a !== void 0 ? _a : '').toLowerCase();
    if (t === 'purchase_credits')
        return true;
    if (t !== 'withdrawal')
        return false;
    const ref = String((_c = (_b = row.referenceType) !== null && _b !== void 0 ? _b : row.reference_type) !== null && _c !== void 0 ? _c : '').toLowerCase();
    if (ref === 'balance_to_credits')
        return true;
    const meta = row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
    return meta.credits_out != null || meta.creditsOut != null;
}
export function usdLedgerTypeLabel(row) {
    var _a, _b;
    const displayLabel = typeof row.displayLabel === 'string' && row.displayLabel.trim()
        ? row.displayLabel.trim()
        : '';
    if (displayLabel)
        return displayLabel;
    if (isPurchaseCreditsRow(row))
        return 'Purchase credits';
    const lt = String((_a = row.ledgerType) !== null && _a !== void 0 ? _a : '').toLowerCase();
    if (lt === 'deposit')
        return 'Deposit';
    if (lt === 'withdraw')
        return 'Withdraw';
    if (lt === 'purchase_credits')
        return 'Purchase credits';
    const t = String((_b = row.type) !== null && _b !== void 0 ? _b : '').toLowerCase();
    if (t === 'deposit')
        return 'Deposit';
    if (t === 'withdrawal')
        return 'Withdraw';
    if (t === 'purchase_credits')
        return 'Purchase credits';
    if (t === 'adjustment') {
        const delta = ledgerBalanceNetDelta(row);
        if (delta != null && delta >= 0)
            return 'Balance Correction — Credit';
        return 'Balance Correction — Debit';
    }
    if (t === 'reversal')
        return 'Transaction Reversal';
    return '—';
}
export function usdLedgerStatusLabel(status) {
    const s = String(status || 'completed').toLowerCase();
    if (s === 'completed')
        return 'Completed';
    if (s === 'pending')
        return 'Pending';
    if (s === 'processing')
        return 'Processing';
    if (s === 'failed')
        return 'Failed';
    if (s === 'cancelled')
        return 'Cancelled';
    return status ? String(status) : '—';
}
export function usdLedgerStatusBadgeClass(status) {
    const s = String(status || 'completed').toLowerCase();
    if (s === 'completed')
        return 'bg-emerald-100 text-emerald-800';
    if (s === 'pending' || s === 'processing')
        return 'bg-amber-100 text-amber-800';
    if (s === 'failed' || s === 'cancelled')
        return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-700';
}
export function usdLedgerPaymentMethodLabel(row) {
    const pm = row.paymentMethod;
    if (pm == null || String(pm).trim() === '')
        return '—';
    return String(pm).trim();
}
const FRIENDLY_CHANNEL_NAMES = {
    BTC: 'Bitcoin',
    BITCOIN: 'Bitcoin',
    LTC: 'Litecoin',
    LITECOIN: 'Litecoin',
    ETH: 'Ethereum',
    ETHEREUM: 'Ethereum',
    DOGE: 'Dogecoin',
    DOGECOIN: 'Dogecoin',
    USDT: 'USDT',
    PYUSD: 'PayPal USD',
};
function formatFriendlyChannel(raw) {
    const s = String(raw || '').trim();
    if (!s)
        return 'External';
    const upper = s.toUpperCase();
    if (FRIENDLY_CHANNEL_NAMES[upper])
        return FRIENDLY_CHANNEL_NAMES[upper];
    if (s === 'BTCPay')
        return 'Bitcoin (BTCPay)';
    if (s === '0xProcessing')
        return 'Crypto (0xProcessing)';
    if (s === 'LinkMePay')
        return 'LinkMePay';
    if (s === 'Cash')
        return 'Cash';
    if (s === 'Internal')
        return 'Internal transfer';
    return s;
}
function normalizeLegacyOwnerLabel(label) {
    return label.replace(/^(Agent|Shop|Player|Super Agent|Platform):\s+/, '$1 · ');
}
/**
 * Plain-language From/To label for non-technical users.
 * Falls back when API still returns legacy "Source: BTC" / "Role: name".
 */
export function usdPartyDisplayLabel(party, row, side) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (!party)
        return '—';
    const rawLabel = (_b = (_a = party.label) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : '';
    const sourceMatch = rawLabel.match(/^Source:\s*(.+)$/i);
    if (sourceMatch) {
        const channel = formatFriendlyChannel(sourceMatch[1]);
        const type = String((_c = row === null || row === void 0 ? void 0 : row.type) !== null && _c !== void 0 ? _c : '').toLowerCase();
        const isDeposit = type === 'deposit';
        const isWithdraw = type === 'withdrawal' || type === 'purchase_credits';
        if (side === 'to' && isWithdraw)
            return `Sent to ${channel}`;
        if (side === 'from' && isDeposit)
            return `Received via ${channel}`;
        if (side === 'from')
            return `Received via ${channel}`;
        if (side === 'to')
            return `Sent to ${channel}`;
        return channel;
    }
    if (rawLabel)
        return normalizeLegacyOwnerLabel(rawLabel);
    if (party.ownerType === 'external') {
        const channel = formatFriendlyChannel(String((_d = party.displayName) !== null && _d !== void 0 ? _d : 'External'));
        const type = String((_e = row === null || row === void 0 ? void 0 : row.type) !== null && _e !== void 0 ? _e : '').toLowerCase();
        if (side === 'to' && (type === 'withdrawal' || type === 'purchase_credits'))
            return `Sent to ${channel}`;
        if (side === 'from' && type === 'deposit')
            return `Received via ${channel}`;
        return channel;
    }
    const role = (_f = party.roleLabel) !== null && _f !== void 0 ? _f : 'Account';
    const name = (_h = (_g = party.displayName) !== null && _g !== void 0 ? _g : party.ownerId) !== null && _h !== void 0 ? _h : 'Unknown';
    return `${role} · ${name}`;
}
/** @deprecated Prefer usdPartyDisplayLabel for table cells */
export function usdPartyLabel(party) {
    return usdPartyDisplayLabel(party);
}
export function ledgerDisplayStatus(row) {
    const st = String(row.status || 'completed').toLowerCase();
    const meta = row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
    const phase = meta.payoutPhase != null ? String(meta.payoutPhase).toLowerCase() : '';
    if (st === 'pending' && phase === 'submitted')
        return 'processing';
    return st;
}
export function ledgerBalanceNetDelta(row) {
    var _a, _b;
    const before = toNum((_a = row.balanceNetBefore) !== null && _a !== void 0 ? _a : row.balance_net_before);
    const after = toNum((_b = row.balanceNetAfter) !== null && _b !== void 0 ? _b : row.balance_net_after);
    if (!Number.isFinite(before) || !Number.isFinite(after))
        return null;
    const delta = after - before;
    return Number.isFinite(delta) ? delta : null;
}
export function signedNetAmount(row) {
    var _a, _b, _c;
    const balanceDelta = ledgerBalanceNetDelta(row);
    if (balanceDelta != null && Math.abs(balanceDelta) > 1e-9) {
        return balanceDelta;
    }
    const t = String((_a = row.type) !== null && _a !== void 0 ? _a : '').toLowerCase();
    const gross = toNum((_b = row.amountGross) !== null && _b !== void 0 ? _b : row.amount_gross);
    const net = toNum((_c = row.amountNet) !== null && _c !== void 0 ? _c : row.amount_net);
    const isOutflow = t === 'withdrawal' || t === 'purchase_credits';
    const amt = isOutflow && Number.isFinite(gross) && gross > 0 ? gross : Number.isFinite(net) ? net : 0;
    if (!Number.isFinite(amt))
        return 0;
    if (isOutflow)
        return -Math.abs(amt);
    return amt;
}
export function balanceAfterNet(row) {
    var _a;
    return toNum((_a = row.balanceNetAfter) !== null && _a !== void 0 ? _a : row.balance_net_after) || 0;
}
export function formatLimitsUsd(n) {
    return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}
/** Summary stats for USD ledger (counts + signed USD net flow). */
export function computeUsdLedgerSummary(rows) {
    let totalDeposits = 0;
    let totalWithdrawals = 0;
    let depositAmount = 0;
    let withdrawalAmount = 0;
    let purchaseCreditsAmount = 0;
    for (const r of rows) {
        const label = usdLedgerTypeLabel(r);
        const signed = signedNetAmount(r);
        const magnitude = Math.abs(signed);
        if (label === 'Deposit') {
            totalDeposits += 1;
            depositAmount += magnitude;
        }
        else if (label === 'Withdraw') {
            totalWithdrawals += 1;
            withdrawalAmount += magnitude;
        }
        else if (label === 'Purchase credits') {
            purchaseCreditsAmount += magnitude;
        }
    }
    return {
        totalTransactions: rows.length,
        totalDeposits,
        totalWithdrawals,
        netFlow: depositAmount - withdrawalAmount - purchaseCreditsAmount,
    };
}
function formatIsoDateLocal(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
/** Resolve calendar date range from period preset or custom inputs. */
export function resolveUsdPeriodDateRange(period, dateFrom, dateTo) {
    const now = new Date();
    if (period === 'today') {
        const t = formatIsoDateLocal(now);
        return { dateFrom: t, dateTo: t };
    }
    if (period === '7d') {
        const from = new Date(now);
        from.setDate(from.getDate() - 6);
        return { dateFrom: formatIsoDateLocal(from), dateTo: formatIsoDateLocal(now) };
    }
    if (period === '30d') {
        const from = new Date(now);
        from.setDate(from.getDate() - 29);
        return { dateFrom: formatIsoDateLocal(from), dateTo: formatIsoDateLocal(now) };
    }
    return { dateFrom: dateFrom.trim(), dateTo: dateTo.trim() };
}
export const USD_LEDGER_MAX_EXPORT_DAYS = 35;
export function validateUsdCustomPeriod(dateFrom, dateTo) {
    if (!dateFrom.trim())
        return 'From date is required.';
    if (!dateTo.trim())
        return 'To date is required.';
    if (dateFrom > dateTo)
        return 'From must be on or before To.';
    return null;
}
/** Export date range: same as custom period + max inclusive calendar days. */
export function validateUsdExportPeriod(dateFrom, dateTo) {
    const base = validateUsdCustomPeriod(dateFrom, dateTo);
    if (base)
        return base;
    const from = new Date(`${dateFrom.trim()}T00:00:00`);
    const to = new Date(`${dateTo.trim()}T00:00:00`);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()))
        return 'Invalid date.';
    const inclusiveDays = Math.floor((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    if (inclusiveDays > USD_LEDGER_MAX_EXPORT_DAYS) {
        return `Date range cannot exceed ${USD_LEDGER_MAX_EXPORT_DAYS} days.`;
    }
    return null;
}
export function formatUsdLedgerNetFlow(n) {
    if (!Number.isFinite(n))
        return '—';
    const abs = Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (n > 1e-9)
        return abs;
    if (n < -1e-9)
        return `-${abs}`;
    return abs;
}
